import { useTranslation } from 'react-i18next';
import { Building2 } from 'lucide-react';
import { Switch } from '@ntconsult/aluma-react';
import { useCreateShare } from '../../hooks/useCreateShare';
import { useRevokeShare } from '../../hooks/useRevokeShare';
import type { Share, ShareResourceType } from '../../types';

interface OrgVisibilityToggleProps {
  resourceType: ShareResourceType;
  resourceId: string;
  orgShare: Share | null;
  canManage: boolean;
}

export function OrgVisibilityToggle({
  resourceType,
  resourceId,
  orgShare,
  canManage,
}: OrgVisibilityToggleProps) {
  const { t } = useTranslation('shares');
  const { mutate: createShare, isPending: isCreating } = useCreateShare();
  const { mutate: revokeShare, isPending: isRevoking } = useRevokeShare();

  const isOn = !!orgShare;

  const toggle = () => {
    if (!canManage) return;
    if (isOn && orgShare) {
      revokeShare(orgShare.id);
    } else {
      createShare({ kind: 'org', resourceType, resourceId });
    }
  };

  return (
    <section className="flex items-center justify-between gap-4">
      <div className="min-w-0">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <Building2 className="w-4 h-4" aria-hidden="true" />
          {t('org.toggleLabel')}
        </h3>
        <p className="mt-1 text-xs text-foreground-muted">
          {t('org.toggleHint')}
        </p>
      </div>
      <Switch
        checked={isOn}
        onCheckedChange={toggle}
        disabled={!canManage || isCreating || isRevoking}
        aria-label={t('org.toggleLabel')}
      />
    </section>
  );
}
